/**
 * SQUAD SLOT → CHARACTER. The whole point of the cast.
 *
 * ── The identity being resolved ─────────────────────────────────────────────
 *
 * The renderer is handed 22 anonymous slots. `sim/roster.ts` documents what a
 * slot index means and it is the only identity in the system:
 *
 *     PlayerId = team * squadSize + slot
 *
 * `RenderState` carries kinematics, `team ∈ {0,1}` and an anim state — no name,
 * no shirt number, no club. So this module is the bridge: given the two clubs a
 * match is between and the positions each side lines up in, it answers "who is
 * slot 14?" with a cast character whose number and kit are already painted into
 * his own textures.
 *
 * ── Why the number is not a decal ──────────────────────────────────────────
 *
 * It is very tempting to draw the shirt number at runtime. It is also wrong for
 * this cast: `roster36.py` writes `number` into the variant config, the builder
 * bakes it into that character's jersey texture, and the model IS the number.
 * There is exactly one way to put the right number on the pitch — put the right
 * MAN on the pitch — and that makes assignment the load-bearing step rather
 * than a lookup.
 *
 * The same is true of the kit: `kit: "home" | "away" | "gk"` is baked. A cast
 * member cannot change strip, so the club a character belongs to is fixed and
 * assignment never crosses clubs.
 *
 * ── Why teams.ts is the source of the squad and this file is not ───────────
 *
 * `ui/teams.ts` already owns squads, formations and positional fit, and its
 * `squadFor()` now returns the real cast for the two modelled clubs (each
 * `SquadPlayer` carrying a `castId`). This module reads THAT, so the squad
 * screen, the commentary booth and the pitch cannot disagree about who is
 * playing. Inventing a second roster here is exactly the failure the brief
 * warns about.
 *
 * ── Degradation ────────────────────────────────────────────────────────────
 *
 * Every function here can answer "nobody" and says why. A slot with no cast
 * character is not a silent substitution: it comes back as `castId: null` with
 * a `reason`, and `castFallbackBanner.ts` puts that reason on the screen.
 * Nothing in this file throws — a match must kick off even when half the art is
 * missing.
 */
import type { Position } from '@fg/shared/sim';
import {
  DEFAULT_MATCH_TEAM_IDS,
  TEAM_ID_CAST_CLUB,
  positionFit,
  squadFor,
  teamOrStub,
  type SquadPlayer,
} from '../../ui/teams.js';
import { castCharacter, castStaff, type CastCharacter } from './castManifest.js';

/** One resolved squad slot. `castId === null` is a fault, never a substitution. */
export interface CastSlot {
  /** PlayerId — the index the renderer indexes `RenderState` with. */
  readonly playerId: number;
  readonly team: 0 | 1;
  /** 0..squadSize-1 within the team. */
  readonly teamSlot: number;
  readonly clubTeamId: string;
  /** The position this slot lines up in, from the match roster. */
  readonly position: Position;
  readonly castId: string | null;
  readonly name: string;
  readonly number: number;
  /** Present only when `castId` is null. Shown to the user, not logged. */
  readonly reason: string | null;
}

export interface CastAssignmentInput {
  /** `[home, away]` club ids. Defaults to the two modelled clubs. */
  readonly teamIds?: readonly [string, string];
  readonly squadSize: number;
  /** Per-team position list, exactly as `sim/roster.ts rosterSlotsFor` returns. */
  readonly slots?: readonly [readonly Position[], readonly Position[]];
}

export interface CastAssignment {
  readonly onPitch: readonly CastSlot[];
  /** Squad members with a model who are not in the XI. Substitutes. */
  readonly bench: readonly (readonly CastSlot[])[];
  readonly teamIds: readonly [string, string];
  /** Every slot that could not be filled, for the banner. */
  readonly faults: readonly string[];
}

/**
 * Greedy positional assignment, keepers first.
 *
 * "Keepers first" is not a tidiness preference. `positionFit` scores GK↔anything
 * as 0, so a greedy pass in slot order can consume the only keeper on an outfield
 * slot's behalf (fit 0 is still the best available once everyone else is taken)
 * and leave the goal to a striker in an outfield strip. Placing the keeper
 * against the GK slot before anything else is what makes that impossible.
 *
 * Ties break on shirt number, so the assignment is deterministic and a rebuild
 * of the cast does not reshuffle the XI.
 */
function assignTeam(
  squad: readonly SquadPlayer[],
  slots: readonly Position[],
): (SquadPlayer | null)[] {
  const pool = squad.filter((p) => p.castId !== undefined);
  const taken = new Set<string>();
  const out: (SquadPlayer | null)[] = new Array(slots.length).fill(null);

  const order = slots
    .map((pos, i) => ({ pos, i }))
    .sort((a, b) => (a.pos === 'GK' ? -1 : 0) - (b.pos === 'GK' ? -1 : 0) || a.i - b.i);

  for (const { pos, i } of order) {
    let best: SquadPlayer | null = null;
    let bestScore = -1;
    for (const p of pool) {
      if (taken.has(p.id)) continue;
      const fit = positionFit(p.position, pos);
      // A keeper is only ever a keeper, in either direction. Without this a
      // team short of outfielders would put the reserve keeper at right-back.
      const usable = (p.position === 'GK') === (pos === 'GK');
      const score = (usable ? 10 : 0) + fit;
      if (score > bestScore || (score === bestScore && best && p.shirt < best.shirt)) {
        best = p;
        bestScore = score;
      }
    }
    if (best && bestScore >= 10) {
      taken.add(best.id);
      out[i] = best;
    }
  }
  return out;
}

export function assignCast(input: CastAssignmentInput): CastAssignment {
  const teamIds = (input.teamIds ?? DEFAULT_MATCH_TEAM_IDS) as readonly [string, string];
  const size = Math.max(1, input.squadSize | 0);
  const onPitch: CastSlot[] = [];
  const bench: CastSlot[][] = [[], []];
  const faults: string[] = [];

  for (let team = 0 as 0 | 1; team <= 1; team = (team + 1) as 0 | 1) {
    const clubTeamId = teamIds[team]!;
    const club = TEAM_ID_CAST_CLUB[clubTeamId];
    const squad = squadFor(clubTeamId);
    const slots =
      input.slots?.[team] ??
      (squad.slice(0, size).map((p) => p.position) as readonly Position[]);
    const picked = club ? assignTeam(squad, slots) : new Array(size).fill(null);

    for (let s = 0; s < size; s++) {
      const p = picked[s] ?? null;
      const pos = (slots[s] ?? 'CM') as Position;
      const playerId = team * size + s;
      if (!p || !p.castId || !castCharacter(p.castId)) {
        const why = !club
          ? `${teamOrStub(clubTeamId).name} has no modelled cast — only ` +
            `${Object.keys(TEAM_ID_CAST_CLUB).join(' and ')} are built`
          : p && p.castId
            ? `cast character "${p.castId}" is in the roster but has no built model`
            : `no cast player fits the ${pos} slot for ${teamOrStub(clubTeamId).name}`;
        onPitch.push({
          playerId, team, teamSlot: s, clubTeamId, position: pos,
          castId: null, name: p?.name ?? '—', number: p?.shirt ?? 0, reason: why,
        });
        faults.push(`slot ${playerId} (${pos}, team ${team}): ${why}`);
        continue;
      }
      onPitch.push({
        playerId, team, teamSlot: s, clubTeamId, position: pos,
        castId: p.castId, name: p.name, number: p.shirt, reason: null,
      });
    }

    // Whoever is left with a model is the bench, in shirt-number order. Their
    // playerId is deliberately negative: they have no slot in the 22-wide sim
    // world (MAX_PLAYERS is 22 and a substitute is not simulated), so a positive
    // id would collide with a man who is actually playing.
    const used = new Set(picked.filter(Boolean).map((p) => p!.id));
    let n = 0;
    for (const p of squad) {
      if (used.has(p.id) || !p.castId || !castCharacter(p.castId)) continue;
      bench[team]!.push({
        playerId: -(team * 100 + ++n), team, teamSlot: size + n - 1, clubTeamId,
        position: p.position, castId: p.castId, name: p.name, number: p.shirt, reason: null,
      });
    }
  }

  return { onPitch, bench: [bench[0]!, bench[1]!], teamIds, faults };
}

// ── staff and officials ─────────────────────────────────────────────────────

/**
 * The two coaches, by which technical area they stand in.
 *
 * The cast has an older coach and a younger one; the older is the home manager
 * because `sideline.ts` gives the home technical area the one ARTICULATED hero
 * figure (it paces and gestures) and the away area an instanced standing figure.
 * Putting the character with real age and a suit on the animated anchor is the
 * one that reads.
 */
export function castCoachFor(side: 'home' | 'away'): CastCharacter | undefined {
  return castStaff(side === 'home' ? 'coach_old' : 'coach_young');
}

export function castOfficial(kind: 'referee' | 'assistant'): CastCharacter | undefined {
  return castStaff(kind);
}

/** Everything the cast can put on or beside a pitch, deduplicated. */
export function castIdsForMatch(a: CastAssignment): readonly string[] {
  const ids = new Set<string>();
  for (const s of a.onPitch) if (s.castId) ids.add(s.castId);
  for (const side of a.bench) for (const s of side) if (s.castId) ids.add(s.castId);
  for (const c of [castCoachFor('home'), castCoachFor('away'),
    castOfficial('referee'), castOfficial('assistant')]) {
    if (c) ids.add(c.id);
  }
  return [...ids];
}
